import { api } from "./apiClient";

function queryString(params = {}) {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    qs.append(key, String(value));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

const clinicAPI = {
  getPublicClinicPortal() {
    return api.get("/public/clinic").then((r) => r.data);
  },
  createPublicTrialLead(data) {
    return api.post("/public/trial-lead", data).then((r) => r.data);
  },
  getWebsiteModule() {
    return api.get("/tenant/modules/website").then((r) => r.data);
  },
  purchaseWebsiteModule() {
    return api.post("/tenant/modules/website/purchase").then((r) => r.data);
  },
  getAnalyticsProModule() {
    return api.get("/tenant/modules/analytics").then((r) => r.data);
  },
  purchaseAnalyticsProModule() {
    return api.post("/tenant/modules/analytics/purchase").then((r) => r.data);
  },
  getPublicClinic(subdomain) {
    return api.get(`/public/clinics/${subdomain}`).then((r) => r.data);
  },
  getPublicSlots(doctorId, branchId, date, serviceId) {
    const qs = new URLSearchParams({
      doctor_id: doctorId,
      branch_id: branchId,
      date,
    });
    if (serviceId) qs.append("service_id", serviceId);
    return api.get(`/public/slots?${qs}`).then((r) => r.data);
  },
  getPublicAggregatedSlots(branchId, date, serviceId) {
    const qs = new URLSearchParams({ branch_id: branchId, date, service_id: serviceId });
    return api.get(`/public/slots/aggregate?${qs}`).then((r) => r.data);
  },
  getBranches() {
    return api.get("/branches").then((r) => r.data);
  },
  getClinicSetupStatus() {
    return api.get("/branches/setup-status").then((r) => r.data);
  },
  createBranch(data) {
    return api.post("/branches", data).then((r) => r.data);
  },
  updateBranch(id, data) {
    return api.patch(`/branches/${id}`, data).then((r) => r.data);
  },
  deleteBranch(id, opts = {}) {
    const params = new URLSearchParams();
    if (opts.cancel_sessions === false) params.set("cancel_sessions", "false");
    if (opts.transfer_to_branch_id) {
      params.set("transfer_to_branch_id", String(opts.transfer_to_branch_id));
    }
    const q = params.toString();
    return api.delete(`/branches/${id}${q ? `?${q}` : ""}`).then((r) => r.data);
  },
  getBranchArchivePreview(id) {
    return api.get(`/branches/${id}/archive-preview`).then((r) => r.data);
  },
  getBranchCatalog(id) {
    return api.get(`/branches/${id}/catalog`).then((r) => r.data);
  },
  setBranchServices(id, service_ids) {
    return api
      .put(`/branches/${id}/services`, { service_ids })
      .then((r) => r.data);
  },
  setBranchSpecializations(id, specialization_ids) {
    return api
      .put(`/branches/${id}/specializations`, { specialization_ids })
      .then((r) => r.data);
  },
  setDoctorBranches(doctorId, branchIds) {
    return api
      .put(`/branches/doctors/${doctorId}`, { branchIds })
      .then((r) => r.data);
  },
  getDoctors(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/trainers${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  getDoctor(id) {
    return api.get(`/trainers/${id}`).then((r) => r.data);
  },
  updateDoctor(id, data) {
    return api.put(`/trainers/${id}`, data).then((r) => r.data);
  },
  deactivateDoctor(id, { replacement_trainer_id } = {}) {
    const qs = new URLSearchParams();
    if (replacement_trainer_id) {
      qs.set("replacement_trainer_id", replacement_trainer_id);
    }
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.delete(`/trainers/${id}${suffix}`).then((r) => r.data);
  },
  searchDoctors(q, limit = 8) {
    const qs = new URLSearchParams({
      q: q.trim(),
      limit: String(limit),
      include_summary: "false",
    }).toString();
    return api.get(`/trainers/search?${qs}`).then((r) => r.data);
  },
  getDoctorSlotPreview(doctorId, { date, branch_id, service_id }) {
    return api
      .get(
        `/trainers/${doctorId}/slots-preview${queryString({ date, branch_id, service_id })}`,
      )
      .then((r) => r.data);
  },
  getDoctorCalendar(doctorId, params) {
    return api
      .get(`/trainers/${doctorId}/calendar${queryString(params)}`)
      .then((r) => r.data);
  },
  getDoctorServices(doctorId) {
    return api.get(`/trainers/${doctorId}/services`).then((r) => r.data);
  },
  updateDoctorServices(doctorId, services) {
    return api
      .put(`/trainers/${doctorId}/services`, { services })
      .then((r) => r.data);
  },
  blockDoctorPeriod(doctorId, data) {
    return api
      .post(`/trainers/${doctorId}/block-period`, data)
      .then((r) => r.data);
  },
  getDoctorClosedOverrides(doctorId, params) {
    return api
      .get(
        `/trainers/${doctorId}/closed-overrides${queryString(params)}`,
      )
      .then((r) => r.data);
  },
  deleteDoctorClosedBulk(doctorId, data) {
    return api
      .delete(`/trainers/${doctorId}/closed-overrides`, { data })
      .then((r) => r.data);
  },
  offerWaitlistSlot(doctorId, entryId, data) {
    return api
      .post(`/trainers/${doctorId}/waitlist/${entryId}/offer-slot`, data)
      .then((r) => r.data);
  },
  duplicateDoctorSchedule(doctorId, data) {
    return api
      .post(`/trainers/${doctorId}/schedule/duplicate`, data)
      .then((r) => r.data);
  },
  getDoctorWorkload(doctorId, month) {
    const qs = month ? `?month=${month}` : "";
    return api.get(`/trainers/${doctorId}/workload${qs}`).then((r) => r.data);
  },
  getDoctorAudit(doctorId) {
    return api.get(`/trainers/${doctorId}/audit`).then((r) => r.data);
  },
  getDoctorManualSlots(doctorId, { date, branch_id }) {
    return api
      .get(
        `/trainers/${doctorId}/manual-slots${queryString({ date, branch_id })}`,
      )
      .then((r) => r.data);
  },
  saveDoctorManualSlots(doctorId, data) {
    return api
      .put(`/trainers/${doctorId}/manual-slots`, data)
      .then((r) => r.data);
  },
  getDoctorWaitlist(doctorId) {
    return api.get(`/trainers/${doctorId}/waitlist`).then((r) => r.data);
  },
  addDoctorWaitlist(doctorId, data) {
    return api
      .post(`/trainers/${doctorId}/waitlist`, data)
      .then((r) => r.data);
  },
  updateDoctorWaitlist(doctorId, entryId, data) {
    return api
      .patch(`/trainers/${doctorId}/waitlist/${entryId}`, data)
      .then((r) => r.data);
  },
  getDoctorReviews(doctorId) {
    return api.get(`/trainers/${doctorId}/reviews`).then((r) => r.data);
  },
  addDoctorReview(doctorId, data) {
    return api.post(`/trainers/${doctorId}/reviews`, data).then((r) => r.data);
  },
  patchDoctorReview(doctorId, reviewId, data) {
    return api
      .patch(`/trainers/${doctorId}/reviews/${reviewId}`, data)
      .then((r) => r.data);
  },
  uploadDoctorPhoto(doctorId, file) {
    const fd = new FormData();
    fd.append("photo", file);
    return api
      .post(`/trainers/${doctorId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  notifyDoctorTomorrow(doctorId) {
    return api
      .post(`/trainers/${doctorId}/notify-tomorrow`)
      .then((r) => r.data);
  },
  getServices() {
    return api.get("/services").then((r) => r.data);
  },
  getServicesCatalog() {
    return api.get("/services/catalog").then((r) => r.data);
  },
  createService(data) {
    return api.post("/services", data).then((r) => r.data);
  },
  updateService(id, data) {
    return api.put(`/services/${id}`, data).then((r) => r.data);
  },
  deactivateService(id) {
    return api.delete(`/services/${id}`).then((r) => r.data);
  },
  setServiceSpecVisibility(id, specializationId, hidden = true) {
    return api
      .patch(`/services/${id}/spec-visibility`, {
        specialization_id: specializationId,
        hidden,
      })
      .then((r) => r.data);
  },
  getSpecializations() {
    return api.get("/specializations").then((r) => r.data);
  },
  createSpecialization(data) {
    return api.post("/specializations", data).then((r) => r.data);
  },
  updateSpecialization(id, data) {
    return api.put(`/specializations/${id}`, data).then((r) => r.data);
  },
  deactivateSpecialization(id, options = {}) {
    const qs = new URLSearchParams();
    if (options.cancel_sessions === false) qs.set("cancel_sessions", "false");
    if (options.transfer_to_group_id) {
      qs.set("transfer_to_group_id", String(options.transfer_to_group_id));
    }
    const query = qs.toString();
    return api
      .delete(`/specializations/${id}${query ? `?${query}` : ""}`)
      .then((r) => r.data);
  },
  getSpecializationArchivePreview(id) {
    return api.get(`/specializations/${id}/archive-preview`).then((r) => r.data);
  },
  getAppointments(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/appointments${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  getBookingCatalog() {
    return api.get("/appointments/booking-catalog").then((r) => r.data);
  },
  getSlots(doctorId, branchId, date, serviceId) {
    const qs = new URLSearchParams({
      doctor_id: doctorId,
      branch_id: branchId,
      date,
    });
    if (serviceId) qs.append("service_id", serviceId);
    return api.get(`/appointments/slots?${qs}`).then((r) => r.data);
  },
  getSlotsRange(doctorId, branchId, from, to, serviceId) {
    const qs = new URLSearchParams({
      doctor_id: doctorId,
      branch_id: branchId,
      from,
      to,
    });
    if (serviceId) qs.append("service_id", serviceId);
    return api.get(`/appointments/slots/range?${qs}`).then((r) => r.data);
  },
  getAggregatedSlots(branchId, date, serviceId) {
    const qs = new URLSearchParams({
      branch_id: branchId,
      date,
      service_id: serviceId,
    });
    return api.get(`/appointments/slots/aggregate?${qs}`).then((r) => r.data);
  },
  getCalendar(params) {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/calendar?${qs}`).then((r) => r.data);
  },
  getSchedule(doctorId, branchId) {
    const qs = branchId ? `?branch_id=${branchId}` : "";
    return api.get(`/schedule/${doctorId}${qs}`).then((r) => r.data);
  },
  setSchedule(doctorId, branchId, slots) {
    return api
      .put(`/schedule/${doctorId}`, { branch_id: branchId, slots })
      .then((r) => r.data);
  },
  addScheduleOverride(doctorId, payload) {
    return api
      .post(`/schedule/${doctorId}/overrides`, payload)
      .then((r) => r.data);
  },
  deleteScheduleOverride(doctorId, overrideId) {
    return api
      .delete(`/schedule/${doctorId}/overrides/${overrideId}`)
      .then((r) => r.data);
  },
  createAppointment(data) {
    return api.post("/appointments", data).then((r) => r.data);
  },
  cancelAppointment(id) {
    return api.put(`/appointments/${id}/cancel`).then((r) => r.data);
  },
  rescheduleAppointment(id, data) {
    return api.patch(`/appointments/${id}/reschedule`, data).then((r) => r.data);
  },
  inviteDoctor(payload) {
    return api.post("/auth/invite-trainer", payload).then((r) => r.data);
  },
  getTenantSettings() {
    return api.get("/tenant/settings").then((r) => r.data);
  },
  getTenantBilling() {
    return api.get("/tenant/billing").then((r) => r.data);
  },
  getTenantSubscriptionCheckout(params) {
    return api.get("/tenant/billing/checkout", { params }).then((r) => r.data);
  },
  initTenantSubscriptionPayment(body = {}) {
    return api.post("/tenant/billing/pay/init", body).then((r) => r.data);
  },
  updateTenantSettings(data) {
    return api.patch("/tenant/settings", data).then((r) => r.data);
  },
  getClubAcquiring() {
    return api.get("/tenant/acquiring").then((r) => r.data);
  },
  updateClubAcquiring(data) {
    return api.patch("/tenant/acquiring", data).then((r) => r.data);
  },
  startYookassaOauth() {
    return api.get("/tenant/acquiring/yookassa/connect").then((r) => r.data);
  },
  disconnectYookassa() {
    return api.post("/tenant/acquiring/yookassa/disconnect").then((r) => r.data);
  },
  disconnectTinkoff() {
    return api.post("/tenant/acquiring/tinkoff/disconnect").then((r) => r.data);
  },
  verifyTinkoff(data = {}) {
    return api.post("/tenant/acquiring/tinkoff/verify", data).then((r) => r.data);
  },
  uploadTenantPortalCover(file) {
    const fd = new FormData();
    fd.append("cover", file);
    return api
      .post("/tenant/settings/portal-cover", fd, { timeout: 60000 })
      .then((r) => r.data);
  },
  uploadTenantPortalLogo(file) {
    const fd = new FormData();
    fd.append("logo", file);
    return api
      .post("/tenant/settings/portal-logo", fd, { timeout: 60000 })
      .then((r) => r.data);
  },
  uploadTenantPortalGallery(file) {
    const fd = new FormData();
    fd.append("cover", file);
    return api
      .post("/tenant/settings/portal-gallery", fd, { timeout: 60000 })
      .then((r) => r.data);
  },
  getAnalyticsOverview(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/analytics/overview${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  getClinicWaitlist(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/waitlist${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  importPatientsCsv(csv) {
    return api.post("/clients/import", { csv }).then((r) => r.data);
  },
  getPatients(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/clients${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  searchPatients(q, limit = 8) {
    const qs = new URLSearchParams({
      q: q.trim(),
      limit: String(limit),
      include_summary: "false",
    }).toString();
    return api.get(`/clients/search?${qs}`).then((r) => r.data);
  },
  getPatientTimeline(id) {
    return api.get(`/clients/${id}/timeline`).then((r) => r.data);
  },
  getPatientAudit(id) {
    return api.get(`/clients/${id}/audit`).then((r) => r.data);
  },
  getPatient(id) {
    return api.get(`/clients/${id}`).then((r) => r.data);
  },
  getMyPatientProfile() {
    return api.get("/clients/me").then((r) => r.data);
  },
  getMyPortalContext() {
    return api.get("/clients/me/portal-context").then((r) => r.data);
  },
  getMyClientDashboard(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/dashboard${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  getMyMembershipPlans(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/membership-plans${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  purchaseMyMembership(data, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .post(`/clients/me/memberships/purchase${qs ? `?${qs}` : ""}`, data)
      .then((r) => r.data);
  },
  getMyClub() {
    return api.get("/clients/me/club").then((r) => r.data);
  },
  getMyNotifications(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/notifications${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  getMyAttendanceStats(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/attendance-stats${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  getMySchedule(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/schedule${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  resolveTrainerMemberProfile(trainerId) {
    return api
      .get(`/clients/me/trainers/${trainerId}/member-profile`)
      .then((r) => r.data);
  },
  getPushPublicKey() {
    return api.get("/clients/me/push/vapid-public-key").then((r) => r.data);
  },
  subscribePush(subscription) {
    return api
      .post("/clients/me/push/subscribe", { subscription })
      .then((r) => r.data);
  },
  unsubscribePush(endpoint) {
    return api
      .delete("/clients/me/push/subscribe", { data: { endpoint } })
      .then((r) => r.data);
  },
  sendTestPush() {
    return api.post("/clients/me/push/test").then((r) => r.data);
  },
  getAdminPushPublicKey() {
    return api.get("/sport/me/push/vapid-public-key").then((r) => r.data);
  },
  subscribeAdminPush(subscription) {
    return api
      .post("/sport/me/push/subscribe", { subscription })
      .then((r) => r.data);
  },
  unsubscribeAdminPush(endpoint) {
    return api
      .delete("/sport/me/push/subscribe", { data: { endpoint } })
      .then((r) => r.data);
  },
  sendTestAdminPush() {
    return api.post("/sport/me/push/test").then((r) => r.data);
  },
  updateMyPatientProfile(data) {
    return api.patch("/clients/me", data).then((r) => r.data);
  },
  uploadMyPatientPhoto(file, params = {}) {
    const fd = new FormData();
    fd.append("photo", file);
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .post(`/clients/me/photo${qs ? `?${qs}` : ""}`, fd, { timeout: 60000 })
      .then((r) => r.data);
  },
  getMyWaitlist() {
    return api.get("/clients/me/waitlist").then((r) => r.data);
  },
  addMyWaitlist(data) {
    return api.post("/clients/me/waitlist", data).then((r) => r.data);
  },
  cancelMyWaitlist(entryId) {
    return api.delete(`/clients/me/waitlist/${entryId}`).then((r) => r.data);
  },
  getFavoriteDoctors() {
    return api.get("/clients/me/favorite-doctors").then((r) => r.data);
  },
  addFavoriteDoctor(doctorId) {
    return api
      .post("/clients/me/favorite-doctors", { doctor_id: doctorId })
      .then((r) => r.data);
  },
  removeFavoriteDoctor(doctorId) {
    return api
      .delete(`/clients/me/favorite-doctors/${doctorId}`)
      .then((r) => r.data);
  },
  getDependents() {
    return api.get("/clients/me/dependents").then((r) => r.data);
  },
  addDependent(data) {
    return api.post("/clients/me/dependents", data).then((r) => r.data);
  },
  updateDependent(id, data) {
    return api.patch(`/clients/me/dependents/${id}`, data).then((r) => r.data);
  },
  deleteDependent(id) {
    return api.delete(`/clients/me/dependents/${id}`).then((r) => r.data);
  },
  updateFamilyAthleteLogin(athleteId, data) {
    return api
      .patch(`/clients/me/family/${athleteId}/login`, data)
      .then((r) => r.data);
  },
  updateFamilyAthleteProfile(athleteId, data) {
    return api
      .patch(`/clients/me/family/${athleteId}`, data)
      .then((r) => r.data);
  },
  inviteFamilyAthletePortal(athleteId, data = {}) {
    return api
      .post(`/clients/me/family/${athleteId}/portal-invite`, data)
      .then((r) => r.data);
  },
  getSupportMessages() {
    return api.get("/clients/me/support").then((r) => r.data);
  },
  createSupportMessage(data) {
    return api.post("/clients/me/support", data).then((r) => r.data);
  },
  getMyChat(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api.get(`/clients/me/chat${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  getClientChatInbox(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/chat/inbox${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  sendMyChat(data, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .post(`/clients/me/chat${qs ? `?${qs}` : ""}`, data)
      .then((r) => r.data);
  },
  getMyChatUnread(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/chat/unread${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  getStaffClientChat(patientId, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/sport/clients/${patientId}/chat${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  sendStaffClientChat(patientId, data) {
    return api.post(`/sport/clients/${patientId}/chat`, data).then((r) => r.data);
  },
  getStaffChatInbox(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/sport/chat/inbox${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  patchStaffChatInboxState(data) {
    return api.patch("/sport/chat/inbox/state", data).then((r) => r.data);
  },
  patchMyChatInboxState(data) {
    return api.patch("/clients/me/chat/inbox/state", data).then((r) => r.data);
  },
  getClubChat(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api.get(`/sport/chat/club${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  sendClubChat(data) {
    return api.post("/sport/chat/club", data).then((r) => r.data);
  },
  getMyClubChat(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/chat/club${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  sendMyClubChat(data) {
    return api.post("/clients/me/chat/club", data).then((r) => r.data);
  },
  getMyChatGroups(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/chat/groups${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  getMyGroupChat(groupId, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/chat/groups/${groupId}${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  sendMyGroupChat(groupId, data, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .post(`/clients/me/chat/groups/${groupId}${qs ? `?${qs}` : ""}`, data)
      .then((r) => r.data);
  },
  getStaffGroupChat(groupId, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/sport/chat/groups/${groupId}${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  sendStaffGroupChat(groupId, data) {
    return api
      .post(`/sport/chat/groups/${groupId}`, data)
      .then((r) => r.data);
  },
  getMyMemberChat(userId, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/chat/members/${userId}${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  getStaffMemberChat(userId, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/sport/chat/members/${userId}${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  getMyMemberChatInbox(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/chat/members${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  sendMyMemberChat(userId, data, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .post(`/clients/me/chat/members/${userId}${qs ? `?${qs}` : ""}`, data)
      .then((r) => r.data);
  },
  sendStaffMemberChat(userId, data) {
    return api
      .post(`/sport/chat/members/${userId}`, data)
      .then((r) => r.data);
  },
  uploadStaffChatMedia(file) {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/sport/chat/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // Видео часто >10с; общий axios timeout иначе рвёт загрузку
        timeout: 180000,
      })
      .then((r) => r.data);
  },
  uploadMyChatMedia(file) {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/clients/me/chat/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      })
      .then((r) => r.data);
  },
  updateMyChatMessage(messageId, data) {
    return api
      .patch(`/clients/me/chat/messages/${messageId}`, data)
      .then((r) => r.data);
  },
  deleteMyChatMessage(messageId) {
    return api
      .delete(`/clients/me/chat/messages/${messageId}`)
      .then((r) => r.data);
  },
  updateStaffChatMessage(messageId, data) {
    return api
      .patch(`/sport/chat/messages/${messageId}`, data)
      .then((r) => r.data);
  },
  deleteStaffChatMessage(messageId) {
    return api
      .delete(`/sport/chat/messages/${messageId}`)
      .then((r) => r.data);
  },
  toggleMyChatMessageReaction(messageId, emoji) {
    return api
      .post(`/clients/me/chat/messages/${messageId}/reactions`, { emoji })
      .then((r) => r.data);
  },
  toggleStaffChatMessageReaction(messageId, emoji) {
    return api
      .post(`/sport/chat/messages/${messageId}/reactions`, { emoji })
      .then((r) => r.data);
  },
  getNotificationPrefs() {
    return api.get("/clients/me/notification-prefs").then((r) => r.data);
  },
  updateNotificationPrefs(data) {
    return api
      .patch("/clients/me/notification-prefs", data)
      .then((r) => r.data);
  },
  getMyPayments(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/payments${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  getMyCompetitions(params = {}) {
    return api.get("/clients/me/competitions", { params }).then((r) => r.data);
  },
  getCompetitionMedals(params = {}) {
    return api
      .get("/clients/me/competition-medals", { params })
      .then((r) => r.data);
  },
  applyToCompetition(sessionId, data = {}, params = {}) {
    return api
      .post(`/clients/me/competitions/${sessionId}/apply`, data, { params })
      .then((r) => r.data);
  },
  getPublicDoctor(doctorId) {
    return api.get(`/public/doctors/${doctorId}`).then((r) => r.data);
  },
  acceptWaitlistOffer(token) {
    return api
      .post("/clients/me/waitlist/accept-offer", { token })
      .then((r) => r.data);
  },
  createPatient(data) {
    return api.post("/clients", data).then((r) => r.data);
  },
  createClientFamily(data) {
    return api.post("/clients/family", data).then((r) => r.data);
  },
  sendClientPortalInvite(patientId) {
    return api.post(`/clients/${patientId}/portal-invite`).then((r) => r.data);
  },
  updatePatient(id, data) {
    return api.patch(`/clients/${id}`, data).then((r) => r.data);
  },
  uploadPatientPhoto(patientId, file) {
    const fd = new FormData();
    fd.append("photo", file);
    return api
      .post(`/clients/${patientId}/photo`, fd, { timeout: 60000 })
      .then((r) => r.data);
  },
  exportPatientsCsv() {
    return api.get("/clients/export", { responseType: "blob" });
  },
  findPatientDuplicates() {
    return api.get("/clients/duplicates").then((r) => r.data);
  },
  mergePatients(sourceId, targetId) {
    return api
      .post("/clients/merge", { source_id: sourceId, target_id: targetId })
      .then((r) => r.data);
  },
  updateAppointmentStatus(id, status, notes) {
    return api
      .patch(`/appointments/${id}/status`, { status, notes })
      .then((r) => r.data);
  },
  exportAppointmentsCsv(from, to) {
    const qs = new URLSearchParams();
    if (from) qs.append("from", from);
    if (to) qs.append("to", to);
    return api.get(`/appointments/export?${qs}`, { responseType: "blob" });
  },
  // ——— BaySport ———
  getSportToday() {
    return api.get("/sport/dashboard/today").then((r) => r.data);
  },
  getMyAdminNotifications() {
    return api.get("/sport/me/notifications").then((r) => r.data);
  },
  markMyAdminNotificationsRead() {
    return api.post("/sport/me/notifications/read").then((r) => r.data);
  },
  markMyNotificationsRead(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .post(`/clients/me/notifications/read${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  getMyTrainerBranding() {
    return api.get("/sport/me/branding").then((r) => r.data);
  },
  updateMyTrainerBranding(data) {
    return api.patch("/sport/me/branding", data).then((r) => r.data);
  },
  uploadMyTrainerPhoto(file) {
    const fd = new FormData();
    fd.append("photo", file);
    return api.post("/sport/me/branding/photo", fd, { timeout: 60000 }).then((r) => r.data);
  },
  uploadMyTrainerCover(file) {
    const fd = new FormData();
    fd.append("cover", file);
    return api.post("/sport/me/branding/cover", fd, { timeout: 60000 }).then((r) => r.data);
  },
  getSportClients(params = {}) {
    return api.get(`/sport/clients${queryString(params)}`).then((r) => r.data);
  },
  getSportClientProfile(patientId) {
    return api.get(`/sport/clients/${patientId}/profile`).then((r) => r.data);
  },
  getStaffClientSocialProfile(patientId) {
    return api
      .get(`/sport/clients/${patientId}/social/profile`)
      .then((r) => r.data);
  },
  getStaffClientSocialWall(patientId, params = {}) {
    return api
      .get(`/sport/clients/${patientId}/social/wall${queryString(params)}`)
      .then((r) => r.data);
  },
  getStaffClientSocialAchievements(patientId, params = {}) {
    return api
      .get(
        `/sport/clients/${patientId}/social/achievements${queryString(params)}`,
      )
      .then((r) => r.data);
  },
  getAthleteGuardians(patientId) {
    return api.get(`/sport/clients/${patientId}/guardians`).then((r) => r.data);
  },
  addAthleteGuardian(patientId, data) {
    return api
      .post(`/sport/clients/${patientId}/guardians`, data)
      .then((r) => r.data);
  },
  removeAthleteGuardian(patientId, guardianId) {
    return api
      .delete(`/sport/clients/${patientId}/guardians/${guardianId}`)
      .then((r) => r.data);
  },
  sendAthleteGuardianInvite(patientId, guardianId) {
    return api
      .post(`/sport/clients/${patientId}/guardians/${guardianId}/invite`)
      .then((r) => r.data);
  },
  getGuardianProfile(guardianUserId) {
    return api.get(`/sport/guardians/${guardianUserId}/profile`).then((r) => r.data);
  },
  getSportDebtors() {
    return api.get("/sport/debtors").then((r) => r.data);
  },
  getSportAnalytics() {
    return api.get("/sport/analytics").then((r) => r.data);
  },
  getSportAnalyticsInsights() {
    return api.get("/sport/analytics/insights").then((r) => r.data);
  },
  getSalesBoard(params = {}) {
    return api.get(`/sport/sales/board${queryString(params)}`).then((r) => r.data);
  },
  getSalesStats(params = {}) {
    const query =
      typeof params === "string" ? { period: params } : params || {};
    if (!query.period) query.period = "30d";
    return api.get("/sport/sales/stats", { params: query }).then((r) => r.data);
  },
  updateSalesStage(data) {
    return api.patch("/sport/sales/stage", data).then((r) => r.data);
  },
  autoDistributeSales(data = {}) {
    return api.post("/sport/sales/auto-distribute", data).then((r) => r.data);
  },
  createSalesLead(data) {
    return api.post("/sport/sales/leads", data).then((r) => r.data);
  },
  getSportAttendanceAnalytics(period = "month") {
    return api
      .get("/sport/analytics/attendance", { params: { period } })
      .then((r) => r.data);
  },
  getAnalyticsProPack(period = "month") {
    return api
      .get("/sport/analytics/pro", { params: { period } })
      .then((r) => r.data);
  },
  listAnalyticsFunnels() {
    return api.get("/sport/analytics/funnels").then((r) => r.data);
  },
  getAnalyticsFunnel(id) {
    return api.get(`/sport/analytics/funnels/${id}`).then((r) => r.data);
  },
  createAnalyticsFunnel(data) {
    return api.post("/sport/analytics/funnels", data).then((r) => r.data);
  },
  updateAnalyticsFunnel(id, data) {
    return api.patch(`/sport/analytics/funnels/${id}`, data).then((r) => r.data);
  },
  deleteAnalyticsFunnel(id) {
    return api.delete(`/sport/analytics/funnels/${id}`).then((r) => r.data);
  },
  getSportFinanceStats(params = {}) {
    return api.get(`/sport/payments/stats${queryString(params)}`).then((r) => r.data);
  },
  getClubExpenses(params = {}) {
    return api.get(`/sport/expenses${queryString(params)}`).then((r) => r.data);
  },
  createClubExpense(data) {
    return api.post("/sport/expenses", data).then((r) => r.data);
  },
  deleteClubExpense(id) {
    return api.delete(`/sport/expenses/${id}`).then((r) => r.data);
  },
  getSessionRegistrations(sessionId) {
    return api.get(`/sport/sessions/${sessionId}/registrations`).then((r) => r.data);
  },
  addSessionRegistration(sessionId, data) {
    return api
      .post(`/sport/sessions/${sessionId}/registrations`, data)
      .then((r) => r.data);
  },
  updateSessionRegistration(sessionId, registrationId, data) {
    return api
      .patch(`/sport/sessions/${sessionId}/registrations/${registrationId}`, data)
      .then((r) => r.data);
  },
  removeSessionRegistration(sessionId, registrationId) {
    return api
      .delete(`/sport/sessions/${sessionId}/registrations/${registrationId}`)
      .then((r) => r.data);
  },
  getSportGroupsSummary() {
    return api.get("/sport/groups").then((r) => r.data);
  },
  getBroadcasts() {
    return api.get("/sport/broadcasts").then((r) => r.data);
  },
  createBroadcast(data) {
    return api.post("/sport/broadcasts", data).then((r) => r.data);
  },
  updateBroadcast(id, data) {
    return api.patch(`/sport/broadcasts/${id}`, data).then((r) => r.data);
  },
  sendBroadcast(id) {
    return api.post(`/sport/broadcasts/${id}/send`).then((r) => r.data);
  },
  deleteBroadcast(id) {
    return api.delete(`/sport/broadcasts/${id}`).then((r) => r.data);
  },
  transferGroupMember(groupId, patientId, targetGroupId) {
    return api
      .post(`/sport/groups/${groupId}/members/${patientId}/transfer`, {
        target_group_id: targetGroupId,
      })
      .then((r) => r.data);
  },
  expelClient(patientId) {
    return api.post(`/sport/clients/${patientId}/expel`).then((r) => r.data);
  },
  softDeleteClient(patientId) {
    return api.delete(`/sport/clients/${patientId}`).then((r) => r.data);
  },
  freezeMembership(id, days) {
    return api
      .patch(`/sport/memberships/${id}/freeze`, { days })
      .then((r) => r.data);
  },
  unfreezeMembership(id) {
    return api
      .patch(`/sport/memberships/${id}/unfreeze`)
      .then((r) => r.data);
  },
  cancelClientMembership(id) {
    return api
      .post(`/sport/memberships/${id}/cancel`)
      .then((r) => r.data);
  },
  activateCoveredMembership(id) {
    return api
      .post(`/sport/memberships/${id}/activate-covered`)
      .then((r) => r.data);
  },
  cancelMyMembership(id, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .post(`/clients/me/memberships/${id}/cancel${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  getPatientSportAttendance(patientId, params = {}) {
    return api
      .get(`/sport/patients/${patientId}/attendance${queryString(params)}`)
      .then((r) => r.data);
  },
  getMembershipPlans(params = {}) {
    return api.get(`/sport/plans${queryString(params)}`).then((r) => r.data);
  },
  createMembershipPlan(data) {
    return api.post("/sport/plans", data).then((r) => r.data);
  },
  updateMembershipPlan(id, data) {
    return api.patch(`/sport/plans/${id}`, data).then((r) => r.data);
  },
  getClientMemberships(params = {}) {
    return api.get(`/sport/memberships${queryString(params)}`).then((r) => r.data);
  },
  createClientMembership(data) {
    return api.post("/sport/memberships", data).then((r) => r.data);
  },
  createClientMembershipsBulk(data) {
    return api.post("/sport/memberships/bulk", data).then((r) => r.data);
  },
  updateClientMembership(id, data) {
    return api.patch(`/sport/memberships/${id}`, data).then((r) => r.data);
  },
  getGroupMembers(groupId) {
    return api.get(`/sport/groups/${groupId}/members`).then((r) => r.data);
  },
  getGroupLeaderboard(groupId, params = {}) {
    return api
      .get(`/sport/groups/${groupId}/leaderboard${queryString(params)}`)
      .then((r) => r.data);
  },
  getGroupAttendanceMatrix(groupId, params = {}) {
    return api
      .get(`/sport/groups/${groupId}/attendance-matrix${queryString(params)}`)
      .then((r) => r.data);
  },
  addGroupMember(groupId, patientId) {
    return api
      .post(`/sport/groups/${groupId}/members`, { patient_id: patientId })
      .then((r) => r.data);
  },
  removeGroupMember(groupId, patientId) {
    return api
      .delete(`/sport/groups/${groupId}/members/${patientId}`)
      .then((r) => r.data);
  },
  getTrainingSessions(params = {}) {
    return api.get(`/sport/sessions${queryString(params)}`).then((r) => r.data);
  },
  createTrainingSession(data) {
    return api.post("/sport/sessions", data).then((r) => r.data);
  },
  updateTrainingSession(id, data) {
    return api.patch(`/sport/sessions/${id}`, data).then((r) => r.data);
  },
  uploadSessionCover(sessionId, file) {
    const fd = new FormData();
    fd.append("cover", file);
    return api.post(`/sport/sessions/${sessionId}/cover`, fd).then((r) => r.data);
  },
  cancelTrainingSession(id, data = {}) {
    return api.post(`/sport/sessions/${id}/cancel`, data).then((r) => r.data);
  },
  getSportDirections() {
    return api.get("/directions").then((r) => r.data);
  },
  createSportDirection(data) {
    return api.post("/directions", data).then((r) => r.data);
  },
  updateSportDirection(id, data) {
    return api.patch(`/directions/${id}`, data).then((r) => r.data);
  },
  deleteSportDirection(id) {
    return api.delete(`/directions/${id}`).then((r) => r.data);
  },
  getGroupScheduleSlots(groupId) {
    return api.get(`/sport/groups/${groupId}/schedule-slots`).then((r) => r.data);
  },
  saveGroupScheduleSlots(groupId, data) {
    return api
      .put(`/sport/groups/${groupId}/schedule-slots`, data)
      .then((r) => r.data);
  },
  assignTrainerGroupSchedule(groupId, data) {
    return api
      .put(`/sport/groups/${groupId}/schedule-slots/trainer-assignments`, data)
      .then((r) => r.data);
  },
  getSessionJournal(sessionId) {
    return api.get(`/sport/sessions/${sessionId}/journal`).then((r) => r.data);
  },
  markSessionAttendance(sessionId, data) {
    return api
      .post(`/sport/sessions/${sessionId}/attendance`, data)
      .then((r) => r.data);
  },
  completeTrainingSession(sessionId) {
    return api.post(`/sport/sessions/${sessionId}/complete`).then((r) => r.data);
  },
  getClientNotes(patientId) {
    return api.get(`/sport/clients/${patientId}/notes`).then((r) => r.data);
  },
  createClientNote(patientId, data) {
    return api.post(`/sport/clients/${patientId}/notes`, data).then((r) => r.data);
  },
  updateClientNote(patientId, noteId, data) {
    return api
      .patch(`/sport/clients/${patientId}/notes/${noteId}`, data)
      .then((r) => r.data);
  },
  getMembershipPayments(params = {}) {
    return api.get(`/sport/payments${queryString(params)}`).then((r) => r.data);
  },
  createMembershipPayment(data) {
    return api.post("/sport/payments", data).then((r) => r.data);
  },
  createSessionFeePayment(data) {
    return api.post("/sport/payments/session-fee", data).then((r) => r.data);
  },
  updateSessionFeeAmount(data) {
    return api.patch("/sport/payments/session-fee", data).then((r) => r.data);
  },
  getSessionFeeSettings() {
    return api.get("/sport/session-fee-settings").then((r) => r.data);
  },
  saveSessionFeeSettings(data) {
    return api.put("/sport/session-fee-settings", data).then((r) => r.data);
  },
  getAbsenceChargeSettings() {
    return api.get("/sport/absence-charge-settings").then((r) => r.data);
  },
  saveAbsenceChargeSettings(data) {
    return api.put("/sport/absence-charge-settings", data).then((r) => r.data);
  },
  initOnlineMembershipPayment(data) {
    return api.post("/sport/payments/online/init", data).then((r) => r.data);
  },
  initOnlineSessionFeePayment(data) {
    return api
      .post("/sport/payments/online/init-session-fee", data)
      .then((r) => r.data);
  },
  getOnlinePaymentAvailable(params = {}) {
    return api
      .get(`/sport/payments/online/available${queryString(params)}`)
      .then((r) => r.data);
  },
  syncOnlineMembershipPayment(data) {
    return api.post("/sport/payments/online/sync", data).then((r) => r.data);
  },
  syncOnlineSessionFeePayment(data) {
    return api
      .post("/sport/payments/online/sync-session-fee", data)
      .then((r) => r.data);
  },
  syncPendingOnlineMembershipPayments(data = {}) {
    return api
      .post("/sport/payments/online/sync-pending", data)
      .then((r) => r.data);
  },
  moderateClientRegistration(patientId, status) {
    return api
      .patch(`/sport/clients/${patientId}/registration`, { status })
      .then((r) => r.data);
  },
  listRegistrationLinks() {
    return api.get("/sport/registration-links").then((r) => r.data);
  },
  getDefaultRegistrationLink(specializationId = null) {
    const params =
      specializationId != null && specializationId !== ""
        ? { specialization_id: specializationId }
        : undefined;
    return api.get("/sport/registration-links/default", { params }).then((r) => r.data);
  },
  createRegistrationLink(data) {
    return api.post("/sport/registration-links", data).then((r) => r.data);
  },
  updateRegistrationLink(id, data) {
    return api.patch(`/sport/registration-links/${id}`, data).then((r) => r.data);
  },
  deleteRegistrationLink(id) {
    return api.delete(`/sport/registration-links/${id}`).then((r) => r.data);
  },
  voidMembershipPayment(paymentId, data = {}) {
    return api
      .post(`/sport/payments/${paymentId}/void`, data)
      .then((r) => r.data);
  },
  getTrainingPolls() {
    return api.get("/sport/polls").then((r) => r.data);
  },
  createTrainingPoll(data) {
    return api.post("/sport/polls", data).then((r) => r.data);
  },
  updateTrainingPoll(id, data) {
    return api.patch(`/sport/polls/${id}`, data).then((r) => r.data);
  },
  deleteTrainingPoll(id) {
    return api.delete(`/sport/polls/${id}`).then((r) => r.data);
  },
  getTrainingPollResults(id) {
    return api.get(`/sport/polls/${id}/results`).then((r) => r.data);
  },
  extendTrainingPoll(id, data = {}) {
    return api.post(`/sport/polls/${id}/extend`, data).then((r) => r.data);
  },
  getSessionPoll(sessionId) {
    return api.get(`/sport/sessions/${sessionId}/poll`).then((r) => r.data);
  },
  getPatientPollHistory(patientId) {
    return api
      .get(`/sport/clients/${patientId}/poll-history`)
      .then((r) => r.data);
  },
  createSessionPoll(sessionId) {
    return api.post(`/sport/sessions/${sessionId}/poll`).then((r) => r.data);
  },
  getMyTrainingPolls(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .get(`/clients/me/training-polls${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
  respondTrainingPoll(dispatchId, payload, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .post(
        `/clients/me/training-polls/${encodeURIComponent(dispatchId)}/respond${qs ? `?${qs}` : ""}`,
        payload,
      )
      .then((r) => r.data);
  },
  postSessionRsvp(sessionId, payload, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== ""),
      ),
    ).toString();
    return api
      .post(
        `/clients/me/sessions/${sessionId}/rsvp${qs ? `?${qs}` : ""}`,
        payload,
      )
      .then((r) => r.data);
  },
  getClubLifePosts(params = {}) {
    return api.get(`/sport/club-life/posts${queryString(params)}`).then((r) => r.data);
  },
  repostClubLifePost(data) {
    return api.post("/sport/club-life/posts/repost", data).then((r) => r.data);
  },
  createClubLifePost(data) {
    return api.post("/sport/club-life/posts", data).then((r) => r.data);
  },
  uploadAdminClubLifePhoto(file) {
    const fd = new FormData();
    fd.append("photo", file);
    return api
      .post("/sport/club-life/upload", fd, { timeout: 60000 })
      .then((r) => r.data);
  },
  updateClubLifePost(id, data) {
    return api.patch(`/sport/club-life/posts/${id}`, data).then((r) => r.data);
  },
  deleteClubLifePost(id) {
    return api.delete(`/sport/club-life/posts/${id}`).then((r) => r.data);
  },
  getAdminClubLifePostComments(postId) {
    return api
      .get(`/sport/club-life/posts/${postId}/comments`)
      .then((r) => r.data);
  },
  toggleAdminClubLifeLike(postId, emoji) {
    return api
      .post(
        `/sport/club-life/posts/${postId}/like`,
        emoji ? { emoji } : {},
      )
      .then((r) => r.data);
  },
  addAdminClubLifeComment(postId, payload) {
    const data = typeof payload === "string" ? { body: payload } : payload;
    return api
      .post(`/sport/club-life/posts/${postId}/comments`, data)
      .then((r) => r.data);
  },
  getClubLifeFeed(params = {}) {
    return api
      .get(`/clients/me/club-life/feed${queryString(params)}`)
      .then((r) => r.data);
  },
  createMyClubLifePost(data) {
    return api.post("/clients/me/club-life/posts", data).then((r) => r.data);
  },
  updateMyClubLifePost(id, data) {
    return api.patch(`/clients/me/club-life/posts/${id}`, data).then((r) => r.data);
  },
  deleteMyClubLifePost(id) {
    return api.delete(`/clients/me/club-life/posts/${id}`).then((r) => r.data);
  },
  uploadClubLifePhoto(file) {
    const fd = new FormData();
    fd.append("photo", file);
    return api
      .post("/clients/me/club-life/upload", fd, { timeout: 60000 })
      .then((r) => r.data);
  },
  toggleClubLifeLike(postId, emoji) {
    return api
      .post(
        `/clients/me/club-life/posts/${postId}/like`,
        emoji ? { emoji } : {},
      )
      .then((r) => r.data);
  },
  getClubLifeComments(postId) {
    return api
      .get(`/clients/me/club-life/posts/${postId}/comments`)
      .then((r) => r.data);
  },
  addClubLifeComment(postId, payload) {
    const data = typeof payload === "string" ? { body: payload } : payload;
    return api
      .post(`/clients/me/club-life/posts/${postId}/comments`, data)
      .then((r) => r.data);
  },
  getMySocialProfile() {
    return api.get("/clients/me/social/profile").then((r) => r.data);
  },
  updateMySocialProfile(payload) {
    return api.patch("/clients/me/social/profile", payload).then((r) => r.data);
  },
  uploadSocialCover(file) {
    const fd = new FormData();
    fd.append("photo", file);
    return api
      .post("/clients/me/social/cover", fd, { timeout: 60000 })
      .then((r) => r.data);
  },
  listSocialMembers(params = {}) {
    return api
      .get(`/clients/me/social/members${queryString(params)}`)
      .then((r) => r.data);
  },
  getSocialMemberProfile(userId) {
    return api
      .get(`/clients/me/social/members/${userId}`)
      .then((r) => r.data);
  },
  getSocialMemberWall(userId, params = {}) {
    return api
      .get(`/clients/me/social/members/${userId}/wall${queryString(params)}`)
      .then((r) => r.data);
  },
  getSocialMemberAchievements(userId, params = {}) {
    return api
      .get(`/clients/me/social/members/${userId}/achievements${queryString(params)}`)
      .then((r) => r.data);
  },
  followSocialMember(userId) {
    return api.post(`/clients/me/social/follow/${userId}`).then((r) => r.data);
  },
  unfollowSocialMember(userId) {
    return api.delete(`/clients/me/social/follow/${userId}`).then((r) => r.data);
  },
  createWallPost(data, params = {}) {
    return api
      .post("/clients/me/social/wall", data, { params })
      .then((r) => r.data);
  },
  updateWallPost(id, data, params = {}) {
    return api
      .patch(`/clients/me/social/wall/${id}`, data, { params })
      .then((r) => r.data);
  },
  deleteWallPost(id, params = {}) {
    return api
      .delete(`/clients/me/social/wall/${id}`, { params })
      .then((r) => r.data);
  },
  repostToWall(data, params = {}) {
    return api
      .post("/clients/me/social/wall/repost", data, { params })
      .then((r) => r.data);
  },
  getSocialFeed(params = {}) {
    return api.get(`/clients/me/social/feed${queryString(params)}`).then((r) => r.data);
  },
  getStaffMemberProfile() {
    return api.get("/sport/me/member-profile").then((r) => r.data);
  },
  updateStaffMemberProfile(data) {
    return api.patch("/sport/me/member-profile", data).then((r) => r.data);
  },
  getStaffMemberWall(params = {}) {
    return api
      .get(`/sport/me/member-wall${queryString(params)}`)
      .then((r) => r.data);
  },
  getStaffMemberAchievements(params = {}) {
    return api
      .get(`/sport/me/member-achievements${queryString(params)}`)
      .then((r) => r.data);
  },
  uploadStaffMemberCover(file) {
    const fd = new FormData();
    fd.append("photo", file);
    return api
      .post("/sport/me/member-cover", fd, { timeout: 60000 })
      .then((r) => r.data);
  },
  uploadStaffMemberPhoto(file) {
    const fd = new FormData();
    fd.append("photo", file);
    return api
      .post("/sport/me/member-photo", fd, { timeout: 60000 })
      .then((r) => r.data);
  },
  createStaffWallPost(data) {
    return api.post("/sport/me/member-wall", data).then((r) => r.data);
  },
  updateStaffWallPost(id, data) {
    return api.patch(`/sport/me/member-wall/${id}`, data).then((r) => r.data);
  },
  deleteStaffWallPost(id) {
    return api.delete(`/sport/me/member-wall/${id}`).then((r) => r.data);
  },
};

export default clinicAPI;
